import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { Validation, ValidationLevel } from '@/api/graphql';

export interface EditableFieldProps {
  label: string;
  /** `AdiProperties` key — also the key validations are reported against. */
  name: string;
  original?: string | null;
  /** The persisted override, if any. */
  editorial?: string | null;
  /** Uncommitted local edit, if the operator has typed something. */
  draft?: string | null;
  onChange: (name: string, value: string) => void;
  onRevert: (name: string) => void;
  validations: Validation[];
  options?: { id: string; name: string }[];
  multiline?: boolean;
  disabled?: boolean;
  /**
   * Constraints handed to the underlying input: `inputMode` for the mobile/soft keyboard, `maxLength`
   * and `placeholder` to show the shape expected.
   *
   * Deliberately not a hard input mask. A provider value that already breaks the format has to remain
   * visible and correctable — a mask would render `2023gfg` as empty or refuse to display it, which is
   * how the corrupted licensing window became unreachable. Validation reports the shape; this only makes
   * the right shape easier to type.
   */
  constrain?: {
    /**
     * `numeric` restricts typing to digits; `decimal` also allows a single dot.
     *
     * `inputMode` on its own was not enough — it only hints the soft keyboard, so on a desktop it does
     * nothing and `4.99jjjjjjj` went straight into the price field. The characters are now filtered as
     * they are typed.
     *
     * Filters **new input only**, and never rejects what is already there: a provider value that is
     * already malformed has to stay visible and correctable. Validation is what reports it.
     */
    inputMode?: 'numeric' | 'decimal' | 'text';
    maxLength?: number;
    placeholder?: string;
  };
}

/**
 * One metadata field, showing the provider's value alongside the operator's override.
 *
 * The whole product hinges on this component being honest about which value is which. Rules it
 * follows, all from CVDS's editor:
 *
 *  - the input shows the **effective** value (override if present, else the provider's);
 *  - when an override exists, the provider's original is shown underneath, so nothing is hidden;
 *  - a revert button appears only when there is something to revert;
 *  - a WARNING renders amber, not red — the `warning` class hooks the theme overrides that do it.
 */
export default function EditableField({
  label,
  name,
  original,
  editorial,
  draft,
  onChange,
  onRevert,
  validations,
  options,
  multiline,
  disabled,
  constrain,
}: EditableFieldProps) {
  const fieldValidations = validations.filter((v) => v.field === name);
  const worst = fieldValidations.some((v) => v.level === ValidationLevel.Error)
    ? ValidationLevel.Error
    : fieldValidations.length
    ? ValidationLevel.Warning
    : undefined;

  const persisted = editorial ?? original ?? '';
  const value = draft ?? persisted;
  const isDirty = draft !== undefined && draft !== null && draft !== persisted;
  const hasOverride = editorial !== null && editorial !== undefined;

  /**
   * The options actually offered, with the current value added when it is not among them.
   *
   * A `Select` whose value matches no option renders **blank**, which silently hid the provider's value:
   * show types arrive as `Series` and `Miniseries`, the configured names are `Series Episode` and
   * `Miniseries Episode`, so those items showed an empty Show type field and looked unset when they were
   * not. The same trap as a date input rendering a malformed date as empty — a value we do not recognise
   * has to stay visible, or an operator cannot see what is wrong, and saving would silently replace it.
   *
   * Labelled so the difference is legible rather than mysterious. It sorts last because it is the odd one
   * out, and it disappears as soon as a configured value is chosen.
   */
  const selectOptions =
    options && value && !options.some((option) => option.id === value)
      ? [...options, { id: value, name: `${value} (not configured)` }]
      : options;

  /**
   * Drop characters the field cannot contain, as they are typed.
   *
   * Applied to what the operator produces, not to what arrives from the provider — the value is only run
   * through this on change, so an existing bad value is displayed untouched and can be corrected.
   */
  const filterInput = (next: string) => {
    if (constrain?.inputMode === 'numeric') {
      return next.replace(/[^0-9]/g, '');
    }
    if (constrain?.inputMode === 'decimal') {
      const digitsAndDots = next.replace(/[^0-9.]/g, '');
      // Keep only the first dot, so "4.9.9" cannot be typed.
      const [head, ...rest] = digitsAndDots.split('.');
      return rest.length ? `${head}.${rest.join('')}` : head;
    }
    return next;
  };

  const helper =
    fieldValidations.map((v) => v.message).join(' · ') ||
    // Names the source rather than saying "original", which reads as "before my edit". This is the
    // value in the provider's delivered document for *this* ingest: `originalProperties` is parsed from
    // `originalXml` and never written again, and a redelivery of the same asset creates a new version
    // with its own. So it is what the provider sent, not necessarily what they sent the first time ever.
    (hasOverride && original ? `Provider sent: ${original}` : ' ');

  return (
    <TextField
      className={worst === ValidationLevel.Warning ? 'warning' : undefined}
      // A predictable anchor, so a validation finding can focus and scroll to this exact field. Also
      // links the label to the input properly, which it was not before.
      id={`field-${name}`}
      label={label}
      value={value}
      onChange={(e) => onChange(name, filterInput(e.target.value))}
      error={!!worst}
      helperText={helper}
      select={!!options}
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      maxRows={multiline ? 8 : undefined}
      disabled={disabled}
      size="small"
      fullWidth
      placeholder={constrain?.placeholder}
      inputProps={
        constrain
          ? { inputMode: constrain.inputMode, maxLength: constrain.maxLength }
          : undefined
      }
      InputProps={
        options
          ? undefined
          : {
              endAdornment: (hasOverride || isDirty) && (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      isDirty
                        ? 'Discard this unsaved change'
                        : "Revert to the provider's value"
                    }
                  >
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => onRevert(name)}
                      disabled={disabled}
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }
      }
      sx={{
        '& .MuiFormHelperText-root': { minHeight: '1.25em' },
        ...(isDirty && {
          // The brand accent, not `secondary`. Secondary is Universal+'s deep purple (#400094),
          // which against a near-black page is effectively invisible — so the one signal telling an
          // operator they have unsaved edits could not be seen. Error stays red and warning stays
          // orange, so the three remain distinguishable.
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
        }),
      }}
    >
      {selectOptions?.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
